/**
 * Campaign type registry — central mapping from the four "campaign type"
 * cards on Step 1 of the Ad Engine Wizard to:
 *
 *   • the sponsor_templates row that should be applied
 *   • the placement enum value (must match `public.ad_placement` in DB)
 *   • the recommended creative aspect ratio for the upload step
 *   • a friendly description, icon, color, and ~daily impressions estimate
 *
 * Adding a new campaign type? Add it here. Every step downstream
 * (Creative dropzone, Targeting defaults, Live preview, Publish summary)
 * reads from this registry, so a single edit propagates everywhere.
 */

import type {
  CampaignTypeKey,
  AspectRatio,
} from '@/stores/campaignWizardStore';

export interface CampaignTypeDef {
  key: CampaignTypeKey;
  label: string;
  tagline: string;
  description: string;
  icon: string;            // emoji for the visual card
  accentClass: string;     // tailwind gradient class for the card border/glow
  templateKey: string;     // matches sponsor_templates.template_key
  placement: string;       // matches public.ad_placement enum value
  estimatedDailyImpressions: number;
  aspectRatio: AspectRatio;       // recommended for the creative upload
  aspectRatioLabel: string;       // e.g. "1:1 square"
  // Acceptable aspect ratios — the upload component warns if the
  // dropped image's ratio falls outside [min..max].
  aspectMin: number;
  aspectMax: number;
  defaultHourStart: number;
  defaultHourEnd: number;
  audienceHint: 'passenger' | 'driver' | 'all';
  isFestival?: boolean;
  /**
   * Fixed frequency caps for this placement. When set, they replace the
   * wizard's shared "per-user/day cap" and "cool-down" on publish.
   */
  fixedMaxPerUserPerDay?: number;
  fixedCoolDownHours?: number;
  goodFor: string[];       // e.g. ["Hotels", "Tour operators"]
  /** UI grouping in the wizard template picker. */
  group: 'splash' | 'sheet' | 'map' | 'home' | 'commute' | 'moments' | 'alerts' | 'festival';
}

export const CAMPAIGN_TYPES: CampaignTypeDef[] = [
  {
    key: 'launch_splash',
    label: 'Launch Splash',
    tagline: 'Full screen when the app opens',
    description:
      'A full-screen sponsor moment the instant a rider opens the app. Skippable, closes itself after 6 seconds, and shows at most once a day per rider. Never shown during a trip. Best for festivals, launches and island-wide campaigns.',
    icon: '✨',
    accentClass: 'from-fuchsia-500/30 to-amber-600/10 border-fuchsia-500/40',
    templateKey: 'launch_splash',
    placement: 'splash_takeover',
    estimatedDailyImpressions: 400,
    aspectRatio: 'tall',
    aspectRatioLabel: '9:16 portrait (full screen)',
    aspectMin: 0.4,
    aspectMax: 0.7,
    defaultHourStart: 0,
    defaultHourEnd: 24,
    audienceHint: 'passenger',
    fixedMaxPerUserPerDay: 1,
    fixedCoolDownHours: 12,
    goodFor: ['Tourism authority', 'Festivals', 'Telecoms', 'Banks'],
    group: 'splash',
  },
  {
    key: 'sponsored_map_pin',
    label: 'Sponsored Map Pin',
    tagline: 'Local retail & hotels',
    description:
      'A featured pin on the passenger map. Tappable card with logo, slogan, address, and "Get directions". Always-on, no time limit. Best for businesses on a corridor.',
    icon: '📍',
    accentClass: 'from-emerald-500/30 to-emerald-700/10 border-emerald-500/40',
    templateKey: 'shop_local_pin',
    placement: 'map_pin_featured',
    estimatedDailyImpressions: 500,
    aspectRatio: 'square',
    aspectRatioLabel: '1:1 square (logo)',
    aspectMin: 0.85,
    aspectMax: 1.15,
    defaultHourStart: 0,
    defaultHourEnd: 24,
    audienceHint: 'passenger',
    goodFor: ['Hotels', 'Restaurants', 'Tour operators', 'Retail shops'],
    group: 'map',
  },
  {
    key: 'passenger_sheet_idle',
    label: 'Sheet Carousel: Where To',
    tagline: 'Bottom of route-search sheet',
    description:
      'Horizontal partner cards at the foot of the "Where to?" sheet while the rider is still choosing a destination. Best for retail, hotels, and corridor businesses.',
    icon: '🧭',
    accentClass: 'from-sky-500/30 to-blue-700/10 border-sky-500/40',
    templateKey: 'passenger_sheet_idle_carousel',
    placement: 'passenger_sheet_idle_carousel',
    estimatedDailyImpressions: 450,
    aspectRatio: 'banner',
    aspectRatioLabel: '3:2 card (wide)',
    aspectMin: 1.2,
    aspectMax: 1.9,
    defaultHourStart: 0,
    defaultHourEnd: 24,
    audienceHint: 'passenger',
    goodFor: ['Retail', 'Hotels', 'Restaurants', 'Banks'],
    group: 'sheet',
  },
  {
    key: 'passenger_sheet_confirm',
    label: 'Sheet Carousel: Confirm Ride',
    tagline: 'Pre-request, high intent',
    description:
      'Partnership strip below fare and Request on the confirm-ride sheet. The rider has locked a route, so it is ideal for telecom bundles, fuel, or food near the corridor.',
    icon: '✅',
    accentClass: 'from-teal-500/30 to-emerald-700/10 border-teal-500/40',
    templateKey: 'passenger_sheet_confirm_carousel',
    placement: 'passenger_sheet_confirm_carousel',
    estimatedDailyImpressions: 380,
    aspectRatio: 'banner',
    aspectRatioLabel: '3:2 card (wide)',
    aspectMin: 1.2,
    aspectMax: 1.9,
    defaultHourStart: 0,
    defaultHourEnd: 24,
    audienceHint: 'passenger',
    goodFor: ['Telecoms', 'Fuel', 'Grocery', 'Pharmacies'],
    group: 'sheet',
  },
  {
    key: 'passenger_sheet_active_ride',
    label: 'Sheet Carousel: Active Ride',
    tagline: 'Searching or on the bus',
    description:
      'Compact carousel while the app is matching a driver or during an active trip. Light-touch offers like snacks, loyalty, and return-trip prompts.',
    icon: '🚌',
    accentClass: 'from-orange-500/30 to-amber-700/10 border-orange-500/40',
    templateKey: 'passenger_sheet_active_ride_carousel',
    placement: 'passenger_sheet_active_ride_carousel',
    estimatedDailyImpressions: 320,
    aspectRatio: 'banner',
    aspectRatioLabel: '3:2 card (wide)',
    aspectMin: 1.2,
    aspectMax: 1.9,
    defaultHourStart: 0,
    defaultHourEnd: 24,
    audienceHint: 'passenger',
    goodFor: ['Restaurants', 'Bars', 'Tour operators', 'Retail'],
    group: 'sheet',
  },
  {
    key: 'passenger_home_banner',
    label: 'Passenger Home Banner',
    tagline: 'Always-on, top of passenger home',
    description:
      'A wide banner pinned to the top of the passenger home screen, shown all day (not limited to rush hours). The single most visible always-on placement in the app. Great for a headline sponsor, hotel, bank, or telecom.',
    icon: '🏠',
    accentClass: 'from-emerald-500/30 to-teal-700/10 border-emerald-500/40',
    templateKey: 'hotel_monthly',
    placement: 'passenger_home_header',
    estimatedDailyImpressions: 700,
    aspectRatio: 'banner',
    aspectRatioLabel: '16:9 wide banner',
    aspectMin: 1.5,
    aspectMax: 2.0,
    defaultHourStart: 0,
    defaultHourEnd: 24,
    audienceHint: 'passenger',
    goodFor: ['Hotels', 'Banks', 'Telecoms', 'Tourism partners'],
    group: 'home',
  },
  {
    key: 'commuter_banner_morning',
    label: 'Morning Commute Banner',
    tagline: 'Time-boxed, 6–10am',
    description:
      'Wide banner on the driver app idle screen during the morning rush (6–10am by default). Drivers only: passengers do not see this placement. Good for telecoms, banks, fuel and auto parts.',
    icon: '🌅',
    accentClass: 'from-amber-500/30 to-orange-600/10 border-amber-500/40',
    templateKey: 'telecom_commute',
    placement: 'commute_morning_takeover',
    estimatedDailyImpressions: 600,
    aspectRatio: 'banner',
    aspectRatioLabel: '16:9 wide banner',
    aspectMin: 1.5,
    aspectMax: 2.0,
    defaultHourStart: 6,
    defaultHourEnd: 10,
    audienceHint: 'all',
    goodFor: ['Telecoms', 'Banks', 'Fuel stations', 'Coffee shops'],
    group: 'commute',
  },
  {
    key: 'commuter_banner_evening',
    label: 'Evening Commute Banner',
    tagline: 'Time-boxed, 4–8pm',
    description:
      'The same driver idle banner, but for the evening rush (4–8pm). Drivers only: passengers do not see this placement. Pairs well with fuel, food and grocery for drivers heading home.',
    icon: '🌇',
    accentClass: 'from-rose-500/30 to-amber-600/10 border-rose-500/40',
    templateKey: 'telecom_commute',
    placement: 'commute_evening_takeover',
    estimatedDailyImpressions: 600,
    aspectRatio: 'banner',
    aspectRatioLabel: '16:9 wide banner',
    aspectMin: 1.5,
    aspectMax: 2.0,
    defaultHourStart: 16,
    defaultHourEnd: 20,
    audienceHint: 'all',
    goodFor: ['Bars', 'Restaurants', 'Grocery', 'Pharmacies'],
    group: 'commute',
  },
  {
    key: 'ride_complete_takeover',
    label: 'Ride-Complete Takeover',
    tagline: 'Full-screen interstitial',
    description:
      'A full-screen card that appears the moment a ride ends. The rider has just arrived, so it is the perfect time to invite them in for a meal, a drink, or to book a return trip.',
    icon: '🍽',
    accentClass: 'from-cyan-500/30 to-emerald-600/10 border-cyan-500/40',
    templateKey: 'restaurant_evening',
    placement: 'ride_complete_full',
    estimatedDailyImpressions: 300,
    aspectRatio: 'tall',
    aspectRatioLabel: '9:16 portrait (full-bleed)',
    aspectMin: 0.4,
    aspectMax: 0.7,
    defaultHourStart: 0,
    defaultHourEnd: 24,
    audienceHint: 'passenger',
    goodFor: ['Restaurants', 'Bars', 'Hotels', 'Spas'],
    group: 'moments',
  },
  {
    key: 'weather_alert',
    label: 'Weather / Disaster Alert',
    tagline: 'Push-style banner + push notification',
    description:
      'A high-contrast safety banner that appears at the top of every screen during weather alerts (rain, hurricane, flood). Optional push notification for serious events. Sponsored by a partner with a public service angle.',
    icon: '⛈',
    accentClass: 'from-blue-500/30 to-cyan-600/10 border-blue-500/40',
    templateKey: 'ferry_weather',
    placement: 'weather_alert_banner',
    estimatedDailyImpressions: 200,
    aspectRatio: 'banner',
    aspectRatioLabel: '16:9 wide banner',
    aspectMin: 1.5,
    aspectMax: 2.5,
    defaultHourStart: 0,
    defaultHourEnd: 24,
    audienceHint: 'all',
    goodFor: ['Insurance', 'Hardware stores', 'Telecoms', 'Government'],
    group: 'alerts',
  },
  {
    key: 'festival_takeover',
    label: 'Festival / Event Takeover',
    tagline: 'Carnival, World Creole, Independence',
    description:
      'A full home-screen takeover that runs only during the dates of a named festival. Headlining sponsor for an event. Higher impressions and a built-in "ends when the festival ends" cutoff.',
    icon: '🎶',
    accentClass: 'from-fuchsia-500/30 to-purple-600/10 border-fuchsia-500/40',
    // NOTE: the frontend `key` is `festival_takeover` but the DB
    // sponsor_templates row was seeded as `festival_event` (see
    // 20260620450000_sponsor_templates_and_wizard.sql). The two are
    // intentionally different — `key` identifies the campaign-type
    // card in the wizard UI, `templateKey` is the FK into
    // sponsor_templates. Keep these in sync with the DB seed or
    // create_campaign_from_wizard returns template_not_found.
    templateKey: 'festival_event',
    placement: 'festival_event_takeover',
    estimatedDailyImpressions: 800,
    aspectRatio: 'tall',
    aspectRatioLabel: '9:16 portrait',
    aspectMin: 0.4,
    aspectMax: 0.7,
    defaultHourStart: 0,
    defaultHourEnd: 24,
    audienceHint: 'all',
    isFestival: true,
    goodFor: ['Telecoms', 'Banks', 'Beverage', 'Tourism partners'],
    group: 'festival',
  },
];

/** Wizard section order — sheet carousels first so operators do not miss them. */
export const CAMPAIGN_TYPE_GROUPS: {
  id: CampaignTypeDef['group'];
  title: string;
  subtitle: string;
}[] = [
  {
    id: 'splash',
    title: 'Launch splash: the biggest moment in the app',
    subtitle:
      'Full screen when a rider opens H-Plate. One sponsor at a time, once a day per rider. Premium price.',
  },
  {
    id: 'sheet',
    title: 'Passenger bottom sheet: in-trip carousels',
    subtitle:
      'DoorDash-style horizontal cards inside the Where to / Confirm / Active ride sheet (scroll the app sheet to see them).',
  },
  {
    id: 'map',
    title: 'Map & discovery',
    subtitle: 'Featured pins and map-first placements.',
  },
  {
    id: 'home',
    title: 'Passenger home',
    subtitle: 'Always-on banner at the top of the passenger home screen.',
  },
  {
    id: 'commute',
    title: 'Commute takeovers',
    subtitle: 'Banners on the driver app idle screen during rush windows. Drivers only.',
  },
  {
    id: 'moments',
    title: 'Ride moments',
    subtitle: 'High-attention surfaces tied to trip lifecycle.',
  },
  {
    id: 'alerts',
    title: 'Weather & safety',
    subtitle: 'System-style banners during weather events.',
  },
];

/**
 * Placements where a client VIDEO creative actually plays in the app.
 *
 * MUST stay in sync with `VIDEO_ALLOWED_PLACEMENTS` in
 * `src/components/monetization/PremiumAdSurface.tsx`. The app only ever
 * autoplays video on these premium, full-attention surfaces; on every
 * other placement a video would be silently downgraded to its still
 * image. So the wizard hides the "Custom video" option everywhere else —
 * offering it would just confuse an operator into uploading a video that
 * never actually plays.
 */
export const VIDEO_CAPABLE_PLACEMENTS = new Set<string>([
  'ride_complete_full',
  'festival_event_takeover',
]);

/** Whether the given campaign type supports a client video creative. */
export function campaignTypeSupportsVideo(
  def?: { placement: string } | null,
): boolean {
  return !!def && VIDEO_CAPABLE_PLACEMENTS.has(def.placement);
}

export function getCampaignTypeByKey(key: string): CampaignTypeDef | undefined {
  return CAMPAIGN_TYPES.find((t) => t.key === key);
}

/**
 * Map an existing template_key (from sponsor_templates) back to the
 * campaign type def. Used when editing an already-published campaign.
 */
export function getCampaignTypeByTemplateKey(
  templateKey: string,
): CampaignTypeDef | undefined {
  return CAMPAIGN_TYPES.find((t) => t.templateKey === templateKey);
}
